import type { Task } from '@/common/api/models/task';
import type { TaskUpdateRequest } from '@/common/api/models/taskUpdateRequest';
import type { TaskObjectives } from '@/common/api/models/taskObjectives';
import type { TaskTypeEnum } from '@/common/api/models/taskTypeEnum';
import type { TaskRubricV2Input } from '@/common/api/models/taskRubricV2Input';
import type { EventTaskConfig } from '@/common/api/models/eventTaskConfig';
import type { TaskEvaluationConfig } from '@/common/api/models/taskEvaluationConfig';
import { isEmptyHtml } from '@/common/utils/htmlContent';
import type { TaskFormData } from './taskEditorSchemas';

/**
 * Keeps task objective form/API data limited to lecturer-editable dimensions.
 */
export const filterAnalysisToEditableDimensions = (
  analysis: Record<string, string[]> | undefined,
  editableDimensionKeys: readonly string[],
): Record<string, string[]> => {
  if (!analysis) return {};
  const editable = new Set(editableDimensionKeys.map((dim) => dim.toLowerCase()));
  return Object.fromEntries(
    Object.entries(analysis)
      .map(([dim, demands]) => [dim.toLowerCase(), demands] as const)
      .filter(([dim]) => editable.has(dim)),
  );
};

/**
 * Maps a Task API object to the TaskFormData used by the editor.
 * This flattens nested structures like 'analysis' for easier form binding.
 */
export const toTaskFormData = (task: Task | null | undefined): TaskFormData | null => {
  if (!task) return null;

  // Flatten analysis dimensions to Record<string, string[]>
  const analysis: Record<string, string[]> = {};
  if (task.analysis?.dimensions) {
    Object.entries(task.analysis.dimensions).forEach(([dim, data]) => {
      analysis[dim] = data.demands || [];
    });
  }

  // Ensure completionMinLevelsOverride values are strings for the selector
  const completionMinLevelsOverride: Record<string, string> = {};
  if (task.evaluation?.completionMinLevelsOverride) {
    Object.entries(task.evaluation.completionMinLevelsOverride).forEach(([key, value]) => {
      completionMinLevelsOverride[key] = String(value);
    });
  }
  const attachmentPolicy = task.evaluation?.attachmentPolicy || 'images';
  const eventConfig = task.eventConfig
    ? {
      ...task.eventConfig,
      // Legacy data may contain null here. Form schema expects string or undefined.
      eventKindId: task.eventConfig.eventKindId ?? undefined,
      durationMinutesOverride: task.eventConfig.durationMinutesOverride ?? null,
      capacityOverride: task.eventConfig.capacityOverride ?? null,
      unlockConditions: task.eventConfig.eventKindId === 'proctored_exam'
        ? { type: 'all_previous', taskIds: [] }
        : task.eventConfig.unlockConditions,
    }
    : null;

  const evalMode = task.evaluation?.mode === 'auto' ? 'auto' : 'manual';

  return {
    id: task.id,
    title: task.title || '',
    description: task.description || '',
    type: (task.type === 'essay' || task.type === 'activity' || task.type === 'event'
      || task.type === 'multiple_choice' || task.type === 'oral_exam'
      ? task.type
      : 'essay'),
    sortIndex: task.sortIndex || 0,
    level: task.level || 1,
    analysis,
    evaluation: {
      ...task.evaluation,
      mode: evalMode,
      attachmentPolicy,
      completionMinLevelsOverride,
    },
    rubric: task.rubric as unknown as TaskRubricV2Input,
    eventConfig: eventConfig as unknown as EventTaskConfig,
    mcqConfig: task.mcqConfig ? {
      questionsToShow: task.mcqConfig.questionsToShow ?? null,
      passThresholdPercent: task.mcqConfig.passThresholdPercent ?? 70,
      shuffleQuestions: task.mcqConfig.shuffleQuestions ?? true,
      shuffleOptions: task.mcqConfig.shuffleOptions ?? true,
      showImmediateFeedback: task.mcqConfig.showImmediateFeedback ?? true,
      timeLimitMinutes: task.mcqConfig.timeLimitMinutes ?? null,
    } : null,
  };
};

/**
 * Maps TaskFormData back to a TaskUpdateRequest object for the API.
 * This reconstructs nested structures and ensures data integrity.
 */
export const toTaskApiRequest = (
  data: TaskFormData,
  editableDimensionKeys?: readonly string[],
): TaskUpdateRequest => {
  const { type } = data;
  const hasEventKindId = Boolean(data.eventConfig?.eventKindId);
  const analysisSource = editableDimensionKeys
    ? filterAnalysisToEditableDimensions(data.analysis, editableDimensionKeys)
    : data.analysis;

  // Reconstruct analysis structure
  const analysis: TaskObjectives = {
    dimensions: {},
  };

  if (analysisSource) {
    Object.entries(analysisSource).forEach(([dim, demands]) => {
      if (!demands || !Array.isArray(demands)) return;
      const validDemands = demands.filter((d) => typeof d === 'string' && d.trim() !== '');

      analysis.dimensions[dim] = {
        applicable: validDemands.length > 0,
        demands: validDemands,
      };
    });
  }

  // Reconstruct completionMinLevelsOverride as Record<string, string> for API
  const completionMinLevelsOverride: Record<string, string> = {};
  if (data.evaluation?.completionMinLevelsOverride) {
    Object.entries(data.evaluation.completionMinLevelsOverride).forEach(([key, value]) => {
      completionMinLevelsOverride[key] = String(value);
    });
  }

  const evaluation: TaskEvaluationConfig | null = data.evaluation && type !== 'activity' ? {
    ...data.evaluation,
    mode: data.evaluation.mode ?? 'manual',
    formalCriteria: data.evaluation.formalCriteria ? {
      wordcount: data.evaluation.formalCriteria.wordcount || 0,
      wordcountTolerance: data.evaluation.formalCriteria.wordcountTolerance || 0,
      minInTextCitations: data.evaluation.formalCriteria.minInTextCitations,
    } : null,
    completionMinLevelsOverride,
  } : null;

  // Build rubric payload for essays. Two important guarantees:
  // 1. `reasoningFlowchart` must be a string (backend `reasoning_flowchart: str`),
  //    never null/undefined. The form schema allows null which would otherwise
  //    cause a silent 422 on save.
  // 2. Do not destructively send `rubric: null` just because the dimensions list
  //    happens to be empty in the form right now. We only null the rubric out
  //    when the user is on a non-essay task type (where rubric is irrelevant)
  //    or when there is genuinely nothing rubric-shaped to persist.
  let rubric: TaskRubricV2Input | null = null;
  if (type === 'essay' && data.rubric) {
    const dimensions = data.rubric.dimensions ?? [];
    const reasoningFlowchart = data.rubric.reasoningFlowchart ?? '';
    if (dimensions.length > 0 || reasoningFlowchart.length > 0) {
      rubric = { reasoningFlowchart, dimensions } as TaskRubricV2Input;
    }
  }

  return {
    title: data.title,
    // Normalize an "empty" rich-text value (e.g. `<p><br></p>`) to a true empty
    // string so the backend clears the field rather than storing a blank paragraph.
    description: isEmptyHtml(data.description) ? '' : data.description,
    type: type as TaskTypeEnum,
    sortIndex: data.sortIndex,
    evaluation,
    analysis,
    rubric,
    // Event config for event, oral exam, and event-delivered MCQ tasks
    eventConfig: (type === 'event' || type === 'oral_exam' || (type === 'multiple_choice' && hasEventKindId))
      && data.eventConfig
      ? ({
        ...data.eventConfig,
        unlockConditions: data.eventConfig.eventKindId === 'proctored_exam'
          ? { type: 'all_previous', taskIds: [] }
          : data.eventConfig.unlockConditions,
      } as EventTaskConfig)
      : null,
  };
};
